import React from 'react'
import { ModalDialog, ModalDialogTitle } from 'uiComponents/popups/modal'
import { PricingType } from 'products/articleConfigurationService'
import { BulkPricingSettingsError } from 'products/pricing/pricingService'
import { ChartDataLoader } from 'uiComponents/loaders'
import { ActionButton } from 'uiComponents/buttons'
import { MessageKind } from 'uiComponents/messages'
import { withCurrency, Currency } from 'uiComponents/money/moneyHoc'
import { FormItem, FormItemName, WrapWithSymbol } from 'uiComponents/form/formElements'
import { ValidationNotice } from 'products/components/validationNotice'
import { Validities } from 'products/pricing/common'
import { PricingServiceContext } from 'products/pricing/context'
import { NumberInput } from 'uiComponents/input'
import { Bold } from 'uiComponents/typography'
import { delay } from 'utils'
import { useTranslation } from 'translations'
import {
    ProductsCard,
    NavigationSection,
    Container,
    TableHeader,
    NameCell,
    Cell,
    TableRow,
} from 'products/bulkEditDialogs/common'

interface ProductInfo {
    name: string
    max: string
    min: string
    avg: string
    gate: string
    id: string
    version: string
}

interface DialogProps {
    onDismiss: () => void
    pricingType: PricingType
    getCurrency: (accountSlug: string) => Currency
    accountSlug: string
    replaceTopMessages?: (id: string, status: MessageKind, text: string) => void
    removeAllMessages?: () => void
    affectedProductInfo: ProductInfo[]
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
    onSave: () => void
}

const initialValidities = {
    BOX: {
        valid: true,
        message: '',
    },
    MIN: {
        valid: true,
        message: '',
    },
    AVG: {
        valid: true,
        message: '',
    },
    GATE: {
        valid: true,
        message: '',
    },
}

export function BulkEditPricingSettingsDialog(props: DialogProps) {
    const { t } = useTranslation()
    const pricingService = React.useContext(PricingServiceContext)
    const currency = props.getCurrency(props.accountSlug)
    const [maximum, setMaximum] = React.useState<number | null>(null)
    const [minimum, setMinimum] = React.useState<number | null>(null)
    const [average, setAverage] = React.useState<number | null>(null)
    const [gate, setGate] = React.useState<number | null>(null)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [validities, setValidities] = React.useState<Validities>(initialValidities)

    const staticOrUpsell = ['static', 'upsell'].includes(props.pricingType)

    const maximumRef = React.useRef<number | null>(null)
    const minimumRef = React.useRef<number | null>(null)
    const averageRef = React.useRef<number | null>(null)
    const gateRef = React.useRef<number | null>(null)
    maximumRef.current = maximum
    minimumRef.current = minimum
    averageRef.current = average
    gateRef.current = gate

    const onSave = async () => {
        if (!checkValidities() && props.replaceTopMessages) {
            props.replaceTopMessages(
                'validation_error',
                'error',
                t('products.bulkEditDialogs.pricingSettings.checkValidationErrors'),
            )
            return
        }
        if (
            !maximum &&
            maximum !== 0 &&
            !minimum &&
            minimum !== 0 &&
            !average &&
            average !== 0 &&
            !gate &&
            props.replaceTopMessages
        ) {
            props.replaceTopMessages('warning', 'warn', t('products.bulkEditDialogs.pricingSettings.fillAtLeastOne'))
            return
        }
        setLoading(true)
        const responseData = await pricingService.sendBulkPricingSettings(
            props.affectedProductInfo.map((p) => ({
                productId: p.id,
                version: p.version,
            })),
            props.accountSlug,
            maximum,
            minimum,
            average,
            gate,
            props.pricingType,
        )
        responseData
            .ifFailure((err: BulkPricingSettingsError) => {
                let additionalMessage = ''
                if (!!err.productIds) {
                    const productNames = props.affectedProductInfo
                        .filter((pi) => err.productIds!.indexOf(pi.id) > -1)
                        .map((pi) => pi.name)
                    additionalMessage = err.productIds
                        ? t('products.bulkEditDialogs.pricingSettings.checkProducts', {
                              products: productNames.join(', '),
                          })
                        : ''
                }
                if (props.replaceTopMessages) {
                    props.replaceTopMessages(err.type, 'error', `${err.message}. ${additionalMessage}`)
                }
                setLoading(false)
            })
            .ifSuccess(async () => {
                props.onSave()
                props.onDismiss()
                setLoading(false)
                if (props.replaceTopMessages && props.removeAllMessages) {
                    props.replaceTopMessages(
                        'success',
                        'success',
                        t('products.bulkEditDialogs.pricingSettings.savedSuccess'),
                    )
                    await delay(3000)
                    props.removeAllMessages()
                }
            })
    }

    const validateMaxAcceptancePrice = () => {
        const value = maximumRef.current
        if (!!value && value < 0) {
            return {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.maxAboveZero'),
            }
        }
        return {
            valid: true,
            message: '',
        }
    }

    const validateMinAcceptancePrice = () => {
        const value = minimumRef.current
        let validity = {
            valid: true,
            message: '',
        }
        if (value === null) {
            return validity
        } else if (maximumRef.current !== null && value > maximumRef.current) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.minBelowMax'),
            }
        } else if (value < 0) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.minAboveZero'),
            }
        }
        return validity
    }

    const validateAvgTargetPrice = () => {
        const value = averageRef.current
        let validity = {
            valid: true,
            message: '',
        }
        if (value === null) {
            return validity
        } else if (maximumRef.current !== null && value > maximumRef.current) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.avgBelowMax'),
            }
        } else if (minimumRef.current !== null && value < minimumRef.current) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.avgAboveMin'),
            }
        } else if (value < 0) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.avgAboveZero'),
            }
        }
        return validity
    }

    const validateGatePrice = () => {
        const value = gateRef.current
        let validity = {
            valid: true,
            message: '',
        }
        if (value === null) {
            return validity
        } else if (maximumRef.current !== null && value < maximumRef.current) {
            validity = {
                valid: false,
                message: staticOrUpsell
                    ? t('products.bulkEditDialogs.pricingSettings.validation.gateAbovePrice')
                    : t('products.bulkEditDialogs.pricingSettings.validation.gateAboveMax'),
            }
        } else if (value < 0) {
            validity = {
                valid: false,
                message: t('products.bulkEditDialogs.pricingSettings.validation.gateAboveZero'),
            }
        }

        return validity
    }

    const checkValidities = () => {
        if (props.removeAllMessages) {
            props.removeAllMessages()
        }
        const maxValidity = validateMaxAcceptancePrice()
        const minValidity = validateMinAcceptancePrice()
        const avgValidity = validateAvgTargetPrice()
        const gateValidity = validateGatePrice()
        setValidities({
            BOX: maxValidity,
            MIN: minValidity,
            AVG: avgValidity,
            GATE: gateValidity,
        })
        return maxValidity.valid && minValidity.valid && avgValidity.valid && gateValidity.valid
    }

    return (
        <ModalDialog onDismiss={props.onDismiss} interactive fromTop="10%" className={loading ? 'noscroll' : ''}>
            {loading && <ChartDataLoader />}
            <Container>
                <ModalDialogTitle>{t('products.bulkEditDialogs.pricingSettings.title')}</ModalDialogTitle>
                {!staticOrUpsell && (
                    <>
                        <FormItem htmlFor="maximum" style={{ position: 'relative' }}>
                            <FormItemName>
                                {t('products.bulkEditDialogs.pricingSettings.maxAcceptedPrice')}
                            </FormItemName>
                            <WrapWithSymbol symbol={currency.symbol} position="left">
                                <NumberInput
                                    align="left"
                                    id="maximum"
                                    name="maximum"
                                    maxLength={7}
                                    max={9999}
                                    value={maximum !== null ? maximum : ''}
                                    onChange={(e) => {
                                        setMaximum(e.target.value === '' ? null : +e.target.value)
                                    }}
                                    onFinishTyping={checkValidities}
                                    status={validities['BOX'].valid ? 'normal' : 'error'}
                                    block
                                />
                            </WrapWithSymbol>
                            <ValidationNotice className={!validities['BOX'].valid ? 'validation-message-visible' : ''}>
                                {validities['BOX'].message}
                            </ValidationNotice>
                        </FormItem>
                        <FormItem htmlFor="minimum" style={{ position: 'relative' }}>
                            <FormItemName>
                                {t('products.bulkEditDialogs.pricingSettings.minAcceptedPrice')}
                            </FormItemName>
                            <WrapWithSymbol symbol={currency.symbol} position="left">
                                <NumberInput
                                    align="left"
                                    id="minimum"
                                    name="minimum"
                                    maxLength={7}
                                    max={9999}
                                    value={minimum !== null ? minimum : ''}
                                    onChange={(e) => setMinimum(e.target.value === '' ? null : +e.target.value)}
                                    onFinishTyping={checkValidities}
                                    status={validities['MIN'].valid ? 'normal' : 'error'}
                                    block
                                />
                            </WrapWithSymbol>
                            <ValidationNotice className={!validities['MIN'].valid ? 'validation-message-visible' : ''}>
                                {validities['MIN'].message}
                            </ValidationNotice>
                        </FormItem>
                        <FormItem htmlFor="average" style={{ position: 'relative' }}>
                            <FormItemName>{t('products.bulkEditDialogs.pricingSettings.averagePrice')}</FormItemName>
                            <WrapWithSymbol symbol={currency.symbol} position="left">
                                <NumberInput
                                    align="left"
                                    id="average"
                                    name="average"
                                    maxLength={7}
                                    max={9999}
                                    value={average !== null ? average : ''}
                                    onChange={(e) => setAverage(e.target.value === '' ? null : +e.target.value)}
                                    onFinishTyping={checkValidities}
                                    status={validities['AVG'].valid ? 'normal' : 'error'}
                                    block
                                />
                            </WrapWithSymbol>
                            <ValidationNotice className={!validities['AVG'].valid ? 'validation-message-visible' : ''}>
                                {validities['AVG'].message}
                            </ValidationNotice>
                        </FormItem>
                    </>
                )}
                {staticOrUpsell && (
                    <FormItem htmlFor="maximum" style={{ position: 'relative' }}>
                        <FormItemName>{t('products.bulkEditDialogs.pricingSettings.price')}</FormItemName>
                        <WrapWithSymbol symbol={currency.symbol} position="left">
                            <NumberInput
                                align="left"
                                id="maximum"
                                name="maximum"
                                maxLength={7}
                                max={9999}
                                value={maximum !== null ? maximum : ''}
                                onChange={(e) => {
                                    setMaximum(e.target.value === '' ? null : +e.target.value)
                                }}
                                onFinishTyping={checkValidities}
                                status={validities['BOX'].valid ? 'normal' : 'error'}
                                block
                            />
                        </WrapWithSymbol>
                        <ValidationNotice className={!validities['BOX'].valid ? 'validation-message-visible' : ''}>
                            {validities['BOX'].message}
                        </ValidationNotice>
                    </FormItem>
                )}
                <FormItem htmlFor="gate" style={{ position: 'relative' }}>
                    <FormItemName>{t('products.bulkEditDialogs.pricingSettings.gatePrice')}</FormItemName>
                    <WrapWithSymbol symbol={currency.symbol} position="left">
                        <NumberInput
                            align="left"
                            id="gate"
                            name="gate"
                            maxLength={7}
                            max={9999}
                            value={gate !== null ? gate : ''}
                            onChange={(e) => setGate(e.target.value === '' ? null : +e.target.value)}
                            onFinishTyping={checkValidities}
                            status={validities['GATE'].valid ? 'normal' : 'error'}
                            block
                        />
                    </WrapWithSymbol>
                    <ValidationNotice className={!validities['GATE'].valid ? 'validation-message-visible' : ''}>
                        {validities['GATE'].message}
                    </ValidationNotice>
                </FormItem>
                <ProductsList
                    affectedProductInfo={props.affectedProductInfo}
                    formatCurrencyString={props.formatCurrencyString}
                    accountSlug={props.accountSlug}
                    staticOrUpsell={staticOrUpsell}
                />
                <NavigationSection>
                    <ActionButton size="large" secondary onClick={props.onDismiss}>
                        {t('common.cancel')}
                    </ActionButton>
                    <ActionButton
                        id="save-settings"
                        size="large"
                        kind="destructive"
                        style={{ marginLeft: '2em' }}
                        onClick={onSave}
                    >
                        {t('common.save')}
                    </ActionButton>
                </NavigationSection>
            </Container>
        </ModalDialog>
    )
}

export default withCurrency(BulkEditPricingSettingsDialog)

interface ListProps {
    affectedProductInfo: ProductInfo[]
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
    accountSlug: string
    staticOrUpsell: boolean
}

function ProductsList(props: ListProps) {
    const { t } = useTranslation()
    return (
        <ProductsCard>
            <div style={{ marginBottom: '1em' }}>{t('products.bulkEditDialogs.pricingSettings.affectedProducts')}</div>
            <TableHeader>
                <NameCell>{t('products.bulkEditDialogs.pricingSettings.productName')}</NameCell>
                {props.staticOrUpsell ? (
                    <>
                        <Cell style={{ width: '6em' }}>{t('products.bulkEditDialogs.pricingSettings.price')}</Cell>
                        <Cell style={{ width: '6em' }}>{t('products.bulkEditDialogs.pricingSettings.gatePrice')}</Cell>
                    </>
                ) : (
                    <>
                        <Cell>{t('products.bulkEditDialogs.pricingSettings.columns.max')}</Cell>
                        <Cell>{t('products.bulkEditDialogs.pricingSettings.columns.min')}</Cell>
                        <Cell>{t('products.bulkEditDialogs.pricingSettings.columns.avg')}</Cell>
                        <Cell>{t('products.bulkEditDialogs.pricingSettings.columns.gate')}</Cell>
                    </>
                )}
            </TableHeader>
            {props.affectedProductInfo.map((el, i) => (
                <TableRow key={i}>
                    <NameCell>
                        <Bold>{el.name}</Bold>
                    </NameCell>
                    {props.staticOrUpsell ? (
                        <>
                            <Cell style={{ width: '6em' }}>
                                {el.max ? props.formatCurrencyString(Number(el.max), props.accountSlug) : '-'}
                            </Cell>
                            <Cell style={{ width: '6em' }}>
                                {el.gate ? props.formatCurrencyString(Number(el.gate), props.accountSlug) : '-'}
                            </Cell>
                        </>
                    ) : (
                        <>
                            <Cell>{el.max ? props.formatCurrencyString(Number(el.max), props.accountSlug) : '-'}</Cell>
                            <Cell>{el.min ? props.formatCurrencyString(Number(el.min), props.accountSlug) : '-'}</Cell>
                            <Cell>{el.avg ? props.formatCurrencyString(Number(el.avg), props.accountSlug) : '-'}</Cell>
                            <Cell>
                                {el.gate ? props.formatCurrencyString(Number(el.gate), props.accountSlug) : '-'}
                            </Cell>
                        </>
                    )}
                </TableRow>
            ))}
        </ProductsCard>
    )
}
